import React, { FunctionComponent } from 'react';
import Image from 'material-ui-image';
import { Box, Grid, Typography } from '@material-ui/core';
import financeSectionStyles from './financeSectionStyles';
import Translation from '../../../../common/components/presentation/Translation/Translation';
import BackToFinanceButton from '../../../common/components/steps/backToFinanceButton/BackToFinanceButton';
import useCheckoutContext from '../../../common/hooks/useCheckoutContext';
import ContinueButton from '../buttons/continueButton/ContinueButton';
import usePlatformConfig from '../../../../common/react/usePlatformConfig';
import useJourneyStepsContext from '../../../checkout4/hooks/useJourneyStepsContext';
import { DxpDataLayerEventNames } from '../../../../../../common/utilities/customDatalayerInterface';
import { EventDataTypes } from '../../../../gql/common/enum/DataLayerEnums';
import useNewDxpDigitalAssignmentEntryEvent from '../../../common/hooks/useNewDxpDigitalAssignmentEntryEvents';
import {
  getCheckoutStepsCommonFields,
  getEventDataCommonFields,
  getPurchaseOptionCommonFields,
  vproductCommonFields,
  removeHostNameFromUrl,
} from '../../../../gql/common/utils/dxpDataLayerCommonFields';

const FinanceSection: FunctionComponent = () => {
  const styles = financeSectionStyles();
  const { deal } = useCheckoutContext();
  const image = deal?.car.images[0].url;
  const {
    platformConfig: { ga4DxpDataLayer },
  } = usePlatformConfig();
  const { jlrDataLayer } = ga4DxpDataLayer ?? {};
  const { steps, activeStep } = useJourneyStepsContext();
  const { callDxpDigitalAssignmentEntryEvent } = useNewDxpDigitalAssignmentEntryEvent();

  const handleClickContinueCTA = () => {
    if (deal && steps && activeStep) {
      callDxpDigitalAssignmentEntryEvent(
        {
          ...getEventDataCommonFields(DxpDataLayerEventNames.PURCHASE_OPTION_NEXT_CTA_CLICK, true),
          event_action: removeHostNameFromUrl(window.location.href),
          event_label: `${EventDataTypes.BUTTON}::${EventDataTypes.NEXT_BUTTON}`,
          event_category: EventDataTypes.CHECKOUT_CLICK,
          event_pagecategory: EventDataTypes.CHECKOUT,
          click_url: removeHostNameFromUrl(window.location.href),
          click_text: EventDataTypes.NEXT_BUTTON,
          click_name: EventDataTypes.BUTTON,
          purchaseOption: {
            price: deal.car.prices?.totalPrice[1]?.money?.amount.toString() ?? '',
            payment_period: deal.finance?.simulation?.loanTermPeriod
              ? `${deal.finance.simulation.loanTermPeriod.value} ${deal.finance.simulation.loanTermPeriod.unit}`
              : '',
            finance_product: deal.finance?.simulation?.financeProductName
              ? deal.finance.simulation.financeProductName.toLowerCase()
              : '',
            down_payment_trade_in_price: '',
            final_deferred_payment: '',
            installment_fee: '',
            total_to_pay: deal.car.prices?.totalPrice[1]?.money?.amount.toString() ?? '',
            monthly_mileage: deal?.finance?.formInput?.mileage?.toString() ?? '',
            vehicle_cash_price: deal?.attributes?.totalPurchaseAmount
              ? deal.attributes.totalPurchaseAmount?.toString().toLowerCase()
              : '',
            ...getPurchaseOptionCommonFields(deal),
          },
          vproduct: {
            availability: deal?.car?.stock ? true : false,
            transmission: deal.car?.engine?.attributes?.transmissionType ?? '',
            max_output: '',
            ...vproductCommonFields(deal),
          },
          checkoutStep: {
            ...getCheckoutStepsCommonFields(steps, activeStep),
          },
        },
        jlrDataLayer,
      );
    }
  };

  return (
    <Grid container spacing={2} data-test={'checkout:finance_section'} className={styles.financeWrapper}>
      <Grid item sm={12} md={6}>
        <Image
          src={image}
          color="transparent"
          className={styles.image}
          imageStyle={{ position: 'unset', height: 'auto' }}
          style={{ paddingTop: 0, position: 'unset' }}
          animationDuration={0}
          data-test={'checkout:finance_section:image'}
          iconContainerStyle={{ position: 'unset' }}
        />
      </Grid>
      <Grid item sm={12} md={6} className={styles.financeDetails}>
        <Typography variant={'h4'} data-test={'checkout:finance_section:title'}>
          <Translation id={'trans__checkout__extras_page__finance_title'} className={styles.title} />
        </Typography>
        <Typography data-test={'checkout:finance_section:details'}>
          <Translation id={'trans__checkout__extras_page__finance_description'} className={styles.details} />
        </Typography>
        <Box className={styles.backToFinanceButtonContainer}>
          <BackToFinanceButton carConfigurationUrl={deal?.car.configurationSourceUrl} />
        </Box>
      </Grid>
      <Grid item sm={12}>
        <Box className={styles.continueButtonContainer}>
          <ContinueButton
            testId="checkout:continue_button:finance"
            activateNextStep
            handleDataLayer={handleClickContinueCTA}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

export default FinanceSection;
